import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Timeline as ActivityIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '@/services/authService';

interface ActivityLog {
  id: number;
  timestamp: string;
  userId?: number;
  username?: string;
  action: string;
  details: string;
  type: 'transaction' | 'user' | 'auth' | 'system';
  severity: 'info' | 'success' | 'warning' | 'error';
}

interface SystemMetrics {
  totalUsers: number;
  activeUsers: number;
  totalTransactions: number;
  pendingTransactions: number;
  completedTransactions: number;
  totalVolume: number;
}

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`activity-tabpanel-${index}`}
      aria-labelledby={`activity-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ActivityMonitoring: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMonitoringData();
  }, [tabValue]);

  // Helper to format timestamps from various API field names safely
  const formatTimestampSafe = (ts?: string | null): string => {
    const raw = ts || '';
    if (!raw) return 'Not provided';
    try {
      const date = new Date(raw);
      if (isNaN(date.getTime())) return 'Not provided';
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Not provided';
    }
  };

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (tabValue === 0) {
        // Fetch transactions as activity logs
        const response = await api.get('/admin/transactions', {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.success) {
          const transactions = response.data.data.transactions || [];
          const logs: ActivityLog[] = transactions.map((t: any) => ({
            id: t.id,
            // support both snake_case and camelCase timestamps from backend
            timestamp: t.created_at || t.createdAt || t.created || t.created_on || null,
            userId: t.userId || t.user_id || (t.user && t.user.id),
            username: t.user?.username || t.username || (t.user && t.user.username),
            action: (t.transactionType || t.transaction_type)?.toUpperCase() || 'TRANSACTION',
            details: `${t.transactionType || t.transaction_type} of ${t.currency} ${t.amount} - ${t.status}`,
            type: 'transaction',
            severity: t.status === 'completed' ? 'success' : t.status === 'failed' ? 'error' : (t.status === 'pending' ? 'warning' : 'info'),
          }));
          setActivityLogs(logs);
        }
      } else if (tabValue === 1) {
        // Fetch recent users as activity logs
        const response = await api.get('/admin/users', {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.success) {
          const users = response.data.data.users || [];
          const logs: ActivityLog[] = users.map((u: any) => ({
            id: u.id,
            timestamp: u.created_at || u.createdAt || u.created || null,
            userId: u.id,
            username: u.username || u.user_name,
            action: 'USER_REGISTRATION',
            details: `New user registered - ${u.registrationStatus || u.registration_status}`,
            type: 'user',
            severity: (u.registrationStatus || u.registration_status) === 'approved' ? 'success' : (u.registrationStatus || u.registration_status) === 'rejected' ? 'error' : 'warning',
          }));
          setActivityLogs(logs);
        }
      } else if (tabValue === 2) {
        // Fetch system metrics
        const response = await api.get('/admin/stats', {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.success) {
          const data = response.data.data;
          setSystemMetrics({
            totalUsers: data.users?.total || 0,
            activeUsers: data.users?.approved || 0,
            totalTransactions: data.transactions?.total || 0,
            pendingTransactions: data.transactions?.pending || 0,
            completedTransactions: data.transactions?.completed || 0,
            totalVolume: (Object.values(data.balances || {}) as (string | number)[]).reduce((sum: number, val: string | number) => sum + (parseFloat(String(val)) || 0), 0) as number,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
      setError('Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'transaction': return <ActivityIcon />;
      case 'user': return <PersonIcon />;
      case 'auth': return <PersonIcon />;
      case 'system': return <ActivityIcon />;
      default: return <InfoIcon />;
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'success': return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'warning': return <WarningIcon sx={{ color: 'warning.main' }} />;
      case 'error': return <ErrorIcon sx={{ color: 'error.main' }} />;
      default: return <InfoIcon sx={{ color: 'info.main' }} />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Activity Monitoring</Typography>
        <Button
          variant="contained"
          onClick={fetchMonitoringData}
          startIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Recent Transactions" />
          <Tab label="User Activity" />
          <Tab label="System Metrics" />
        </Tabs>
      </Box>

      {/* Recent Transactions */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            {activityLogs.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  No transactions found
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {(() => {
                            try {
                              const date = new Date(log.timestamp);
                              if (isNaN(date.getTime())) {
                                return 'Invalid Date';
                              }
                              return format(date, 'MMM dd, yyyy HH:mm');
                            } catch (e) {
                              return 'Invalid Date';
                            }
                          })()}
                        </TableCell>
                        <TableCell>{log.username || 'N/A'}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getActivityIcon(log.type)}
                            {log.action}
                          </Box>
                        </TableCell>
                        <TableCell>{log.details}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.severity} 
                            size="small" 
                            color={log.severity as any}
                            icon={getSeverityIcon(log.severity)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* User Activity */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            {activityLogs.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  No user activity found
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {(() => {
                            try {
                              const date = new Date(log.timestamp);
                              if (isNaN(date.getTime())) {
                                return 'Invalid Date';
                              }
                              return format(date, 'MMM dd, yyyy HH:mm');
                            } catch (e) {
                              return 'Invalid Date';
                            }
                          })()}
                        </TableCell>
                        <TableCell>{log.username}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getActivityIcon(log.type)}
                            {log.action}
                          </Box>
                        </TableCell>
                        <TableCell>{log.details}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.severity} 
                            size="small" 
                            color={log.severity as any}
                            icon={getSeverityIcon(log.severity)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* System Metrics */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h4" color="primary">
                  {systemMetrics?.totalUsers || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Active Users
                </Typography>
                <Typography variant="h4" color="success.main">
                  {systemMetrics?.activeUsers || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Total Transactions
                </Typography>
                <Typography variant="h4" color="info.main">
                  {systemMetrics?.totalTransactions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Pending Transactions
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {systemMetrics?.pendingTransactions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Completed Transactions
                </Typography>
                <Typography variant="h4" color="success.main">
                  {systemMetrics?.completedTransactions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Total Volume
                </Typography>
                <Typography variant="h4" color="primary">
                  €{(systemMetrics?.totalVolume || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default ActivityMonitoring;
